<template>
  <div class="lpq-root">

    <!-- Navbar -->
    <header class="lpq-navbar" :class="{ 'lpq-navbar--scrolled': scrolled }">
      <div class="lpq-container lpq-navbar__inner">
        <router-link to="/">
          <img src="@/assets/images/logo/mexico_verde2.png" alt="Portal México Verde" class="lpq-navbar__logo" />
        </router-link>
        <nav class="lpq-navbar__right">
          <router-link to="/" class="lpq-link-back">← Inicio</router-link>
          <router-link to="/auth/login" class="lpq-btn lpq-btn--outline">Ingresar</router-link>
          <router-link to="/auth/register" class="lpq-btn lpq-btn--primary">Registrarse</router-link>
        </nav>
      </div>
    </header>

    <!-- Hero -->
    <section class="lpq-hero">
      <div class="lpq-container">
        <span class="lpq-eyebrow">Diagnóstico gratuito</span>
        <h1>Diagnóstico de reverdecimiento empresarial</h1>
        <p>Responde cada pregunta con honestidad para obtener un resultado preciso sobre el nivel de sustentabilidad de tu empresa.</p>
      </div>
    </section>

    <!-- Cuerpo -->
    <main class="lpq-main">
      <div class="lpq-container">

        <!-- Cargando -->
        <div v-if="loading" class="lpq-state lpq-state--loading">
          <div class="lpq-spinner"></div>
          <p>Cargando cuestionario…</p>
        </div>

        <!-- Error de carga -->
        <div v-else-if="errorCarga" class="lpq-state">
          <div class="lpq-alert lpq-alert--danger">
            {{ errorCarga }}
            <button class="lpq-btn-link" @click="fetchCuestionario">Reintentar</button>
          </div>
        </div>

        <!-- ═══════════  PREGUNTA ACTIVA  ═══════════ -->
        <div v-else-if="!enRevision && preguntaActual" class="lpq-card">

          <!-- Cabecera con progreso -->
          <div class="lpq-card__header">
            <div class="lpq-progress-meta">
              <span class="lpq-step">
                Pregunta <strong>{{ currentIndex + 1 }}</strong> de {{ preguntas.length }}
              </span>
              <span class="lpq-answered">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style="vertical-align:-.1em;margin-right:.3rem">
                  <circle cx="6.5" cy="6.5" r="6.5" fill="rgba(102,187,106,.35)"/>
                  <path d="M3.5 6.5l2 2 4-4" stroke="#66bb6a" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>{{ respondidas }} respondidas
              </span>
            </div>
            <div class="lpq-progress">
              <div class="lpq-progress__bar" :style="{ width: progreso + '%' }"></div>
            </div>
          </div>

          <!-- Pregunta y opciones -->
          <div class="lpq-card__body lpq-card__body--question">
            <p class="lpq-q-text">{{ preguntaActual.texto }}</p>

            <div class="lpq-options">
              <button
                v-for="(opcion, idx) in preguntaActual.opciones"
                :key="opcion.id"
                class="lpq-option"
                :class="{ 'lpq-option--selected': respuestas[preguntaActual.id] === opcion.id }"
                @click="seleccionarOpcion(preguntaActual.id, opcion.id)"
              >
                <span class="lpq-option__letra">{{ letraOpcion(idx) }}</span>
                <span class="lpq-option__texto">{{ opcion.texto }}</span>
                <span class="lpq-option__check">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2.5 7l3 3 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </button>
            </div>

            <p v-if="errorPreguntaActual" class="lpq-q-error">
              Selecciona una opción para continuar.
            </p>
          </div>

          <!-- Navegación -->
          <div class="lpq-card__footer lpq-card__footer--nav">
            <button
              class="lpq-btn lpq-btn--ghost-dark"
              :disabled="currentIndex === 0"
              @click="anterior()"
            >
              ← Anterior
            </button>
            <button class="lpq-btn lpq-btn--primary lpq-btn--lg" @click="siguiente()">
              {{ esUltimaPregunta ? 'Revisar respuestas →' : 'Siguiente →' }}
            </button>
          </div>
        </div>

        <!-- ═══════════  REVISIÓN  ═══════════ -->
        <div v-else-if="enRevision" class="lpq-card">

          <div class="lpq-card__header">
            <h2>Revisa tus respuestas</h2>
            <p>Verifica que todo esté correcto antes de enviar. Haz clic en cualquier respuesta para editarla.</p>
          </div>

          <div class="lpq-review-list">
            <div
              v-for="(pregunta, idx) in preguntas"
              :key="pregunta.id"
              class="lpq-review-item"
              :class="{ 'lpq-review-item--vacio': !respuestas[pregunta.id] }"
              @click="editarRespuesta(idx)"
            >
              <div class="lpq-review-item__num">{{ idx + 1 }}</div>
              <div class="lpq-review-item__content">
                <p class="lpq-review-item__q">{{ pregunta.texto }}</p>
                <p class="lpq-review-item__a">
                  <span v-if="respuestas[pregunta.id]" style="color:#2e7d32;">
                    ✓ {{ labelOpcion(pregunta, respuestas[pregunta.id]) }}
                  </span>
                  <span v-else style="color:#c62828;">
                    ✗ Sin respuesta — haz clic para responder
                  </span>
                </p>
              </div>
              <span class="lpq-review-item__edit">✎</span>
            </div>
          </div>

          <div v-if="errorEnvio" class="lpq-alert lpq-alert--danger" style="margin: 0 2rem;">
            {{ errorEnvio }}
          </div>

          <div class="lpq-card__footer lpq-card__footer--nav">
            <button class="lpq-btn lpq-btn--ghost-dark" @click="volverDeRevision()">
              ← Volver
            </button>
            <div style="display:flex; flex-direction:column; align-items:flex-end; gap:.4rem;">
              <button
                class="lpq-btn lpq-btn--primary lpq-btn--lg"
                :disabled="enviando || tieneRespuestasIncompletas"
                @click="enviarRespuestas()"
              >
                <span v-if="enviando" class="lpq-btn-spinner"></span>
                {{ enviando ? 'Evaluando…' : 'Obtener mi diagnóstico →' }}
              </button>
              <span v-if="tieneRespuestasIncompletas" style="font-size:.75rem; color:#c62828;">
                Hay preguntas sin responder
              </span>
              <span class="lpq-footer-note" v-else>
                Al enviar aceptas el uso de tus respuestas para generar tu diagnóstico.
              </span>
            </div>
          </div>
        </div>

      </div>
    </main>

    <!-- Footer -->
    <footer class="lpq-footer">
      <div class="lpq-container lpq-footer__inner">
        <span>© {{ currentYear }} Portal México Verde · AMSDE</span>
        <div class="lpq-footer__links">
          <router-link to="/">Inicio</router-link>
          <router-link to="/auth/register">Crear cuenta</router-link>
          <router-link to="/auth/login">Ingresar</router-link>
        </div>
      </div>
    </footer>

  </div>
</template>

<script>
import { getGuestToken, createPublicApi } from "@/services/guestAuth";

export default {
  name: "DiagnosticoQuizPublico",

  data() {
    return {
      titulo:              "Cuestionario de diagnóstico",
      descripcion:         "",
      preguntas:           [],
      respuestas:          {},
      loading:             false,
      errorCarga:          null,
      enviando:            false,
      errorEnvio:          null,
      scrolled:            false,
      currentYear:         new Date().getFullYear(),
      currentIndex:        0,
      enRevision:          false,
      errorPreguntaActual: false,
      autoAdvanceTimer:    null,
    };
  },

  computed: {
    preguntaActual() {
      return this.preguntas[this.currentIndex] ?? null;
    },
    esUltimaPregunta() {
      return this.currentIndex === this.preguntas.length - 1;
    },
    respondidas() {
      return this.preguntas.filter((p) => this.respuestas[p.id] != null).length;
    },
    progreso() {
      if (!this.preguntas.length) return 0;
      return Math.round((this.respondidas / this.preguntas.length) * 100);
    },
    tieneRespuestasIncompletas() {
      return this.preguntas.some((p) => !this.respuestas[p.id]);
    },
  },

  created() {
    this.fetchCuestionario();
  },

  mounted() {
    window.addEventListener("scroll", this.onScroll);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll);
    clearTimeout(this.autoAdvanceTimer);
  },

  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 40;
    },

    letraOpcion(idx) {
      return String.fromCharCode(65 + idx);
    },

    labelOpcion(pregunta, opcionId) {
      return pregunta.opciones?.find((o) => o.id === opcionId)?.texto ?? '';
    },

    seleccionarOpcion(pregId, opcionId) {
      this.respuestas = { ...this.respuestas, [pregId]: opcionId };
      this.errorPreguntaActual = false;
      if (!this.esUltimaPregunta) {
        clearTimeout(this.autoAdvanceTimer);
        this.autoAdvanceTimer = setTimeout(() => this.siguiente(true), 380);
      }
    },

    siguiente(autoAvance = false) {
      if (!autoAvance && !this.respuestas[this.preguntaActual?.id]) {
        this.errorPreguntaActual = true;
        return;
      }
      this.errorPreguntaActual = false;
      if (this.esUltimaPregunta) {
        this.enRevision = true;
      } else {
        this.currentIndex++;
      }
    },

    anterior() {
      this.errorPreguntaActual = false;
      clearTimeout(this.autoAdvanceTimer);
      if (this.currentIndex > 0) this.currentIndex--;
    },

    volverDeRevision() {
      this.enRevision = false;
      this.currentIndex = this.preguntas.length - 1;
    },

    editarRespuesta(idx) {
      this.enRevision = false;
      this.currentIndex = idx;
    },

    async callWithToken(fn) {
      let token = await getGuestToken();
      try {
        return await fn(createPublicApi(token));
      } catch (err) {
        if (err.response?.status === 401) {
          token = await getGuestToken({ force: true });
          return await fn(createPublicApi(token));
        }
        throw err;
      }
    },

    async fetchCuestionario() {
      this.loading = true;
      this.errorCarga = null;
      try {
        const data = await this.callWithToken((api) =>
          api.get("/diagnostico/api/cuestionarios/1").then((r) => r.data)
        );
        this.titulo      = data.titulo ?? "Cuestionario de diagnóstico";
        this.descripcion = data.descripcion ?? "";
        this.preguntas   = data.preguntas ?? [];
        this.respuestas  = {};
        this.preguntas.forEach((p) => { this.respuestas[p.id] = null; });
      } catch (err) {
        this.errorCarga = err.response?.data?.detail ?? "Error al cargar el cuestionario. Intenta de nuevo.";
      } finally {
        this.loading = false;
      }
    },

    async enviarRespuestas() {
      if (this.tieneRespuestasIncompletas) return;
      this.enviando  = true;
      this.errorEnvio = null;
      try {
        const payload = {
          respuestas: this.preguntas.map((p) => ({
            pregunta_id: p.id,
            opcion_id:   this.respuestas[p.id],
          })),
        };
        const data = await this.callWithToken((api) =>
          api.post("/diagnostico/api/cuestionarios/1/responder/", payload).then((r) => r.data)
        );
        sessionStorage.setItem("diagnostico_resultado", JSON.stringify(data));
        this.$router.push({ name: "diagnosticoPublicoResultado" });
      } catch (err) {
        this.errorEnvio =
          err.response?.data?.detail ?? "Error al enviar las respuestas. Intenta de nuevo.";
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Base ──────────────────────────────────────────────────────────────── */
.lpq-root {
  font-family: 'Segoe UI', system-ui, sans-serif;
  color: #1a1a2e; min-height: 100vh; display: flex; flex-direction: column;
}
.lpq-container { max-width: 720px; margin: 0 auto; padding: 0 1.5rem; }

/* ── Buttons ───────────────────────────────────────────────────────────── */
.lpq-btn {
  display: inline-flex; align-items: center; gap: .4rem;
  padding: .6rem 1.4rem; border-radius: 4px; font-weight: 600;
  font-size: .9rem; text-decoration: none; transition: all .2s;
  border: 2px solid transparent; cursor: pointer;
}
.lpq-btn--primary        { background: #AF1F23; color: #fff; border-color: #AF1F23; }
.lpq-btn--primary:hover  { background: #8e1519; border-color: #8e1519; }
.lpq-btn--outline        { background: transparent; color: #fff; border-color: rgba(255,255,255,.6); }
.lpq-btn--outline:hover  { background: rgba(255,255,255,.12); }
.lpq-btn--ghost-dark     { background: transparent; color: #555; border-color: #d1d5db; }
.lpq-btn--ghost-dark:hover { background: #f0f2f5; }
.lpq-btn--ghost-dark:disabled { opacity: .4; cursor: not-allowed; }
.lpq-btn--lg             { padding: .85rem 2.25rem; font-size: 1rem; }
.lpq-btn:disabled        { opacity: .6; cursor: not-allowed; }
.lpq-btn-link            { background: none; border: none; color: #AF1F23; font-weight: 600; cursor: pointer; padding: 0 .25rem; }
.lpq-btn-spinner {
  display: inline-block; width: 16px; height: 16px;
  border: 2px solid rgba(255,255,255,.4); border-top-color: #fff;
  border-radius: 50%; animation: lpqSpin .7s linear infinite;
}
@keyframes lpqSpin { to { transform: rotate(360deg); } }

/* ── Navbar ────────────────────────────────────────────────────────────── */
.lpq-navbar {
  position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
  background: transparent; transition: background .3s, box-shadow .3s;
}
.lpq-navbar--scrolled { background: #09377c; box-shadow: 0 2px 16px rgba(0,0,0,.25); }
.lpq-navbar__inner {
  display: flex; align-items: center; justify-content: space-between;
  height: 68px; max-width: 1140px; margin: 0 auto; padding: 0 1.5rem;
}
.lpq-navbar__logo { height: 34px; object-fit: contain; filter: brightness(0) invert(1); }
.lpq-navbar__right { display: flex; align-items: center; gap: 1rem; }
.lpq-link-back { color: rgba(255,255,255,.75); font-size: .88rem; text-decoration: none; transition: color .2s; }
.lpq-link-back:hover { color: #fff; }

/* ── Hero ──────────────────────────────────────────────────────────────── */
.lpq-hero {
  background: linear-gradient(135deg, #0b1f3a 0%, #09377c 55%, #1a5276 100%);
  padding: 8rem 0 3.5rem; text-align: center;
}
.lpq-eyebrow {
  display: inline-block; background: rgba(175,31,35,.25); color: #fff;
  border: 1px solid rgba(175,31,35,.5); padding: .25rem .9rem;
  border-radius: 20px; font-size: .75rem; font-weight: 700;
  letter-spacing: .1em; text-transform: uppercase; margin-bottom: 1rem;
}
.lpq-hero h1 {
  font-size: clamp(1.6rem, 4vw, 2.5rem); font-weight: 800; color: #fff; margin: 0 0 1rem;
}
.lpq-hero p { color: rgba(255,255,255,.72); font-size: 1.05rem; line-height: 1.7; max-width: 560px; margin: 0 auto; }

/* ── Main ──────────────────────────────────────────────────────────────── */
.lpq-main { flex: 1; background: #f5f7fa; padding: 3rem 0 4rem; }

/* ── State ─────────────────────────────────────────────────────────────── */
.lpq-state { text-align: center; padding: 3rem 0; color: #666; }
.lpq-state--loading p { margin-top: 1rem; }
.lpq-spinner {
  width: 40px; height: 40px; border: 3px solid #e2e8f0;
  border-top-color: #09377c; border-radius: 50%;
  animation: lpqSpin .8s linear infinite; margin: 0 auto;
}

/* ── Alert ─────────────────────────────────────────────────────────────── */
.lpq-alert {
  padding: .85rem 1.25rem; border-radius: 6px; font-size: .9rem;
  display: flex; align-items: center; gap: .75rem;
}
.lpq-alert--danger { background: #fdecea; color: #c62828; border: 1px solid #ef9a9a; }

/* ── Card shell ────────────────────────────────────────────────────────── */
.lpq-card {
  background: #fff; border-radius: 12px;
  box-shadow: 0 4px 28px rgba(9,55,124,.10); overflow: hidden;
}

/* ── Card header ───────────────────────────────────────────────────────── */
.lpq-card__header {
  background: linear-gradient(135deg, #09377c 0%, #1e59a2 100%);
  padding: 1.5rem 2rem 1.25rem;
}
.lpq-card__header h2 { color: #fff; font-size: 1.15rem; font-weight: 700; margin: 0 0 .3rem; }
.lpq-card__header p  { color: rgba(255,255,255,.78); font-size: .88rem; margin: 0; }

/* ── Progress ──────────────────────────────────────────────────────────── */
.lpq-progress-meta {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: .6rem;
}
.lpq-step         { color: rgba(255,255,255,.75); font-size: .88rem; }
.lpq-step strong  { color: #fff; font-size: 1.05rem; }
.lpq-answered     { color: rgba(255,255,255,.65); font-size: .8rem; }
.lpq-progress {
  height: 6px; background: rgba(255,255,255,.2); border-radius: 3px; overflow: hidden;
}
.lpq-progress__bar {
  height: 100%; background: #66bb6a; border-radius: 3px; transition: width .4s ease;
}

/* ── Card body ─────────────────────────────────────────────────────────── */
.lpq-card__body--question { padding: 2rem 2rem 1.5rem; }

/* ── Question text ─────────────────────────────────────────────────────── */
.lpq-q-text {
  font-size: 1.1rem; font-weight: 600; color: #1a1a2e;
  line-height: 1.65; margin-bottom: 1.75rem;
}
.lpq-q-error { color: #c62828; font-size: .83rem; margin: .6rem 0 0; }

/* ── Option buttons ────────────────────────────────────────────────────── */
.lpq-options { display: flex; flex-direction: column; gap: .6rem; }
.lpq-option {
  display: flex; align-items: center; gap: 1rem;
  width: 100%; padding: .9rem 1.25rem;
  border: 2px solid #e2e8f0; border-radius: 8px;
  background: #fff; color: #1a1a2e; font-size: .93rem;
  text-align: left; cursor: pointer;
  transition: border-color .15s, background .15s, box-shadow .15s;
}
.lpq-option:hover {
  border-color: #09377c; background: #f0f5ff;
  box-shadow: 0 2px 8px rgba(9,55,124,.08);
}
.lpq-option--selected {
  border-color: #09377c; background: #09377c; color: #fff;
  box-shadow: 0 3px 12px rgba(9,55,124,.25);
}
.lpq-option__letra {
  flex-shrink: 0; width: 28px; height: 28px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: .78rem;
  background: rgba(9,55,124,.1); color: #09377c;
  transition: background .15s, color .15s;
}
.lpq-option--selected .lpq-option__letra { background: rgba(255,255,255,.2); color: #fff; }
.lpq-option__texto { flex-grow: 1; line-height: 1.4; }
.lpq-option__check { flex-shrink: 0; opacity: 0; color: #fff; transition: opacity .15s; }
.lpq-option--selected .lpq-option__check { opacity: 1; }

/* ── Card footer ───────────────────────────────────────────────────────── */
.lpq-card__footer--nav {
  display: flex; justify-content: space-between; align-items: center;
  padding: 1.25rem 2rem; background: #f8fafc; border-top: 1px solid #e2e8f0;
}
.lpq-footer-note { font-size: .72rem; color: #aaa; }

/* ── Review list ───────────────────────────────────────────────────────── */
.lpq-review-list { border-top: 1px solid #f0f2f5; }
.lpq-review-item {
  display: flex; align-items: flex-start; gap: 1rem;
  padding: 1rem 2rem; border-bottom: 1px solid #f0f2f5;
  cursor: pointer; transition: background .15s;
}
.lpq-review-item:last-child { border-bottom: none; }
.lpq-review-item:hover { background: #f8fafc; }
.lpq-review-item--vacio { background: #fff8f8; }
.lpq-review-item--vacio:hover { background: #fdecea; }

.lpq-review-item__num {
  flex-shrink: 0; width: 26px; height: 26px; border-radius: 50%;
  background: #09377c; color: #fff; font-size: .72rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center; margin-top: .1rem;
}
.lpq-review-item--vacio .lpq-review-item__num { background: #c62828; }
.lpq-review-item__content { flex-grow: 1; }
.lpq-review-item__q { font-size: .86rem; font-weight: 600; color: #1a1a2e; margin: 0 0 .2rem; line-height: 1.4; }
.lpq-review-item__a { font-size: .82rem; color: #555; margin: 0; }
.lpq-review-item__edit { color: #bbb; font-size: 1rem; margin-top: .2rem; transition: color .15s; }
.lpq-review-item:hover .lpq-review-item__edit { color: #09377c; }

/* ── Footer ────────────────────────────────────────────────────────────── */
.lpq-footer { background: #0b1f3a; padding: 1.5rem 0; margin-top: auto; }
.lpq-footer__inner {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 1rem; max-width: 1140px; margin: 0 auto; padding: 0 1.5rem;
}
.lpq-footer__inner > span { color: rgba(255,255,255,.4); font-size: .8rem; }
.lpq-footer__links { display: flex; gap: 1.5rem; }
.lpq-footer__links a { color: rgba(255,255,255,.55); font-size: .82rem; text-decoration: none; transition: color .2s; }
.lpq-footer__links a:hover { color: #fff; }

/* ── Responsive ────────────────────────────────────────────────────────── */
@media (max-width: 600px) {
  .lpq-card__body--question { padding: 1.5rem 1.25rem; }
  .lpq-card__footer--nav    { padding: 1rem 1.25rem; flex-wrap: wrap; gap: .75rem; }
  .lpq-card__footer--nav .lpq-btn--ghost-dark { order: 2; }
  .lpq-card__footer--nav > div { order: 1; width: 100%; }
  .lpq-review-item          { padding: .85rem 1.25rem; }
  .lpq-navbar__right .lpq-btn { display: none; }
}
</style>